/**
 * Global search palette (tracker item 61).
 *
 * A modal opened from AppLayout (Ctrl/Cmd-K or the search buttons). The
 * input is an ARIA combobox that owns a listbox of results grouped by entity
 * type; ArrowUp / ArrowDown move the active option (wrapping), Enter opens
 * it, Escape or a backdrop click closes the palette. Queries are debounced
 * and an older response never replaces a newer one. The backend decides
 * which entity types the signed-in role may see.
 *
 * Render it only while open: unmounting resets its state, restores focus to
 * whatever had it before, and releases the body scroll lock.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAPIClient, ISearchApi } from '../api';
import { GLOBAL_SEARCH_MIN_LENGTH, GlobalSearchGroup, GlobalSearchResult } from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { globalSearchResultPath } from '../utils/globalSearch';

/** Wait this long after the last keystroke before searching. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 250;
/** Results per entity type. */
const RESULTS_PER_TYPE = 5;

const LISTBOX_ID = 'global-search-listbox';
const optionId = (index: number) => `global-search-option-${index}`;

type Status = 'idle' | 'loading' | 'done' | 'error';

interface GlobalSearchPaletteProps {
  onClose: () => void;
}

export function GlobalSearchPalette({ onClose }: GlobalSearchPaletteProps) {
  const navigate = useNavigate();
  const api = useMemo<ISearchApi>(() => createAPIClient(), []);
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([]);
  const [searched, setSearched] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** Bumped by every new search (and on cleanup), so a stale response is dropped. */
  const requestSeq = useRef(0);

  const trimmed = query.trim();

  // Focus the input, lock body scroll, and give focus back on close.
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previous && previous.isConnected) previous.focus();
    };
  }, []);

  // Escape closes from anywhere in the page while the palette is open.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Debounced search; out-of-order responses are ignored.
  useEffect(() => {
    if (trimmed.length < GLOBAL_SEARCH_MIN_LENGTH) {
      requestSeq.current += 1;
      setGroups([]);
      setSearched('');
      setStatus('idle');
      setError('');
      return undefined;
    }
    setStatus('loading');
    const timer = window.setTimeout(() => {
      const seq = ++requestSeq.current;
      api.globalSearch(trimmed, { limit: RESULTS_PER_TYPE }).then(
        (response) => {
          if (seq !== requestSeq.current) return;
          setGroups(response.groups.filter((g) => g.results.length > 0));
          setSearched(trimmed);
          setActive(0);
          setError('');
          setStatus('done');
        },
        (err: unknown) => {
          if (seq !== requestSeq.current) return;
          setGroups([]);
          setError(getErrorMessage(err, 'Search failed. Try again.'));
          setStatus('error');
        }
      );
    }, GLOBAL_SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      requestSeq.current += 1;
    };
  }, [api, trimmed]);

  const options: GlobalSearchResult[] = useMemo(() => groups.flatMap((g) => g.results), [groups]);
  const activeIndex = options.length === 0 ? -1 : Math.min(active, options.length - 1);

  // Keep the active option in view.
  useEffect(() => {
    if (activeIndex < 0) return;
    const element = listRef.current?.querySelector<HTMLElement>(`#${optionId(activeIndex)}`);
    element?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const open = (result: GlobalSearchResult) => {
    onClose();
    navigate(globalSearchResultPath(result));
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (options.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((activeIndex + step + options.length) % options.length);
    } else if (event.key === 'Home' && options.length > 0 && event.ctrlKey) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End' && options.length > 0 && event.ctrlKey) {
      event.preventDefault();
      setActive(options.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0) open(options[activeIndex]);
    } else if (event.key === 'Tab') {
      // The input is the dialog's only focusable control: keep focus inside.
      event.preventDefault();
    }
  };

  const showList = status === 'done' && options.length > 0;
  let message = '';
  if (trimmed.length < GLOBAL_SEARCH_MIN_LENGTH) message = `Type at least ${GLOBAL_SEARCH_MIN_LENGTH} characters to search.`;
  else if (status === 'loading') message = 'Searching…';
  else if (status === 'error') message = error;
  else if (status === 'done' && options.length === 0) message = `No results for “${searched}”.`;
  else if (status === 'done') message = `${options.length} result${options.length === 1 ? '' : 's'}.`;

  let index = -1;

  return (
    <div className="print:hidden fixed inset-0 z-50 flex items-start justify-center px-4 pt-[10vh] sm:pt-[15vh]">
      <div aria-hidden="true" data-testid="global-search-backdrop" onClick={onClose} className="absolute inset-0 bg-gray-900/50" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="relative w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-black/5"
      >
        <div className="flex items-center gap-2 border-b border-gray-200 px-4">
          <SearchIcon className="h-5 w-5 shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label="Search customers, properties, jobs, invoices, payments and estimates"
            aria-autocomplete="list"
            aria-expanded={showList}
            aria-controls={LISTBOX_ID}
            aria-activedescendant={showList && activeIndex >= 0 ? optionId(activeIndex) : undefined}
            autoComplete="off"
            spellCheck={false}
            placeholder="Search customers, jobs, invoices…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            className="min-h-[52px] w-full border-0 bg-transparent text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
          />
          <button
            type="button"
            onClick={onClose}
            tabIndex={-1}
            className="shrink-0 rounded border border-gray-300 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100"
          >
            Esc
          </button>
        </div>

        <ul
          ref={listRef}
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Search results"
          className={showList ? 'max-h-[60vh] overflow-y-auto py-2' : 'hidden'}
        >
          {showList &&
            groups.map((group) => {
              const headingId = `global-search-group-${group.type}`;
              return (
                <li key={group.type} role="presentation">
                  <ul role="group" aria-labelledby={headingId}>
                    <li role="presentation" id={headingId} className="px-4 pb-1 pt-2 text-xs font-bold uppercase tracking-wider text-gray-500">
                      {group.label}
                    </li>
                    {group.results.map((result) => {
                      index += 1;
                      const i = index;
                      const selected = i === activeIndex;
                      return (
                        <li
                          key={`${result.type}:${result.id}`}
                          id={optionId(i)}
                          role="option"
                          aria-selected={selected}
                          data-type={result.type}
                          onMouseMove={() => { if (!selected) setActive(i); }}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => open(result)}
                          className={`mx-2 cursor-pointer rounded-md px-3 py-2 ${selected ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`}
                        >
                          <div className="truncate text-sm font-medium">{result.title}</div>
                          {result.subtitle && <div className="truncate text-xs text-gray-500">{result.subtitle}</div>}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
        </ul>

        <p
          role="status"
          aria-live="polite"
          data-testid="global-search-status"
          className={`px-4 py-3 text-sm ${status === 'error' ? 'text-red-700' : 'text-gray-500'} ${showList ? 'sr-only' : ''}`}
        >
          {message}
        </p>

        <div className="hidden sm:flex items-center gap-4 border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500">
          <span><kbd className="font-sans">↑</kbd> <kbd className="font-sans">↓</kbd> to move</span>
          <span><kbd className="font-sans">Enter</kbd> to open</span>
          <span><kbd className="font-sans">Esc</kbd> to close</span>
        </div>
      </div>
    </div>
  );
}

export function SearchIcon({ className }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className={className}>
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" d="M20 20l-3.5-3.5" />
    </svg>
  );
}
